import {
  type AxiosResponse,
} from 'axios';
import { defineStore } from 'pinia';
import { computed, ref, toRaw, toValue, watch } from 'vue';

import { usePagination } from '@/common/composables/paginate';
import { mixPanelEvents } from '@/common/constants';
import { useAuthStore } from '@/common/stores/auth';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track as trackEvent } from '@/helpers/analytics/mixpanel';
import { axios, sanctumCookie } from '@/helpers/axios';
import { storage } from '@/helpers/local-storage';
import { SEARCH_QUERY_KEY } from '@/modules/listings/constants';

import {
  listingNameColumnConfig,
  NAME_ACTIVE_TABLE_CONFIG_COLUMNS,
  NAME_ACTIVE_TABLE_PAGINATION_PREFERENCES_PAGE,
  NAME_ACTIVE_TABLE_PAGINATION_PREFERENCES_ROWS,
  NAME_ACTIVE_TABLE_SORT_PREFERENCES,
  NAME_DRAFT_TABLE_CONFIG_COLUMNS,
  NAME_DRAFT_TABLE_PAGINATION_PREFERENCES_PAGE,
  NAME_DRAFT_TABLE_PAGINATION_PREFERENCES_ROWS,
  NAME_DRAFT_TABLE_SORT_PREFERENCES,
  NAME_EXPIRED_TABLE_CONFIG_COLUMNS,
  NAME_EXPIRED_TABLE_PAGINATION_PREFERENCES_PAGE,
  NAME_EXPIRED_TABLE_PAGINATION_PREFERENCES_ROWS, 
  NAME_EXPIRED_TABLE_SORT_PREFERENCES,
  NAME_SOLD_OUT_TABLE_CONFIG_COLUMNS,
  NAME_SOLD_OUT_TABLE_PAGINATION_PREFERENCES_PAGE,
  NAME_SOLD_OUT_TABLE_PAGINATION_PREFERENCES_ROWS,
  NAME_SOLD_OUT_TABLE_SORT_PREFERENCES,
  NAME_THUMBNAIL_PREVIEW_TABLE_PAGINATION_PREFERENCES_PAGE,
  NAME_THUMBNAIL_PREVIEW_TABLE_PAGINATION_PREFERENCES_ROWS,
  PAGE_NUMBER,
  ROWS_PER_PAGE,
  SHOP_SECTION_ID,
  SHOW_TRACKING_ONLY,
  TABLE_LISTINS_CONFIG_COLUMNS_ACTIVE,
  TABLE_LISTINS_CONFIG_COLUMNS_DRAFT,
  TABLE_LISTINS_CONFIG_COLUMNS_EXPIRED,
  TABLE_LISTINS_CONFIG_COLUMNS_SOLD_OUT,
} from '../constants';
import type {
  ConfigListingColumns,
  ListingItem,
  ListingNameColumnConfig,
  ListingPropertyName,
  ListingState,
  OrderDirection,
  PayloadListings,
  SortPreferences,
} from '../types';

interface SectionItem {
  shop_section_id: number;
  section_name: string;
  active_listing_count: number;
}

interface PaginatorComponent {
  setPage: (page: number) => void;
  resetPerPage: (defaultValue?: number) => void;
}

export const useListingTable = defineStore('listingTable', () => {

  const isLoading = ref<boolean>(true);
  const isLoadingSections = ref<boolean>(true);
  const dataListings = ref<ListingItem[]>([]);
  const customColumns = ref<ConfigListingColumns>(null);
  const sections = ref<SectionItem[]>([]);

  // Tracking
  const trackingQuota = ref<number>(0);
  const trackingUsage = ref<number>(0);
  const showNoTrackingListings = ref<boolean>(false);
  const { currentPlan, currentShopId } = useAuthStore();
  const toast = useTrackedToast();
  const checkedTrackingOnly = ref<boolean>(false);
  const keepShowingTrackingAndSection = ref<boolean>(false);
  const paginatorComponentRef = ref<PaginatorComponent | null>(null);
  const userReachTrackingQuota = ref<boolean>(false); // Used to block more tracking requests when the user has reached the quota

  const isFreePlan = currentPlan === 'free';

  const activeCount = ref<number>(0);
  const draftCount = ref<number>(0);
  const expiredCount = ref<number>(0);
  const soldOutCount = ref<number>(0);

  const tabBadges = computed(() => ({
    'active': activeCount.value,
    'draft': draftCount.value,
    'expired': expiredCount.value,
    'sold_out': soldOutCount.value,
  }));

  const totalPageRecords = ref<number>(0);
  const shopSectionId = ref<number | null>(null); // Keep null if no section selected, this is how backend is waiting

  const currentRequestListingData = ref<AbortController | null>(null);
  const quickSearch = ref<string>('');
  const quickSearchListingId = ref<string>('');
  const trackingListAll = ref<ListingItem[]>([]); // Temporaly store all tracking listings
  const { rowsPerPage } = usePagination(totalPageRecords);

  const sortBy = ref<ListingPropertyName | null>(null);
  const sortDirection = ref<OrderDirection>('asc');

  const currentGlobalPage = ref<number>(1);
  const currentState = ref<ListingState>('active');
  const currentRowsLimit = ref<number>(10);
  const currentOffset = ref<number>(0);

  const getSections = async () => {

    if (!currentShopId) {
      return;
    }

    try {

      isLoadingSections.value = true;

      const response = await axios.get(`/${currentShopId}/shop-sections`);

      if (typeof response.data.error === 'undefined') {
        const { data } = response.data;
        sections.value = data || [];
      } else {
        sections.value = [];
      }

    } catch (e) {

      sections.value = [];

    } finally {

      isLoadingSections.value = false;

    }

  };

  const getListingItems = async () => {

    let response: AxiosResponse;

    isLoading.value = true;

    dataListings.value = [];

    try {

      // Check if there's already a request in progress and abort it
      if (currentRequestListingData.value) {
        currentRequestListingData.value.abort();
      }

      currentRequestListingData.value = new AbortController();

      const signal = currentRequestListingData.value.signal;
      const searchTermToSend = quickSearchListingId.value && !quickSearch.value
        ? quickSearchListingId.value
        : quickSearch.value;

      rowsPerPage.value = getRowsPerPage();

      if (!checkedTrackingOnly.value) {
        const payload: PayloadListings = {
          shop_id: null, // Saved in laravel session
          limit: currentRowsLimit.value,
          offset: currentOffset.value,
          order_by: sortBy.value ?? 'title',
          sort_direction: sortDirection.value ?? 'asc',
          shop_section_id: shopSectionId.value,
          search_term: searchTermToSend,
          advance_filter_option: null,
        };

        if (shopSectionId.value !== null) {
          keepShowingTrackingAndSection.value = true;
        }

        if (quickSearch.value.length > 0 || quickSearchListingId.value.length > 0){
          keepShowingTrackingAndSection.value = true;
        }
        await sanctumCookie();
        response = await axios.post(`/listings/${currentState.value}`, payload, { signal });

        const { data, count } = response.data;

        totalPageRecords.value = count;

        updateTabBadge(currentState.value, count);

        dataListings.value = data.map((item: ListingItem, index: number) => ({ ...item, id: currentOffset.value + index + 1 }));

      } else {

        keepShowingTrackingAndSection.value = true; // When there are no records, a message no record is displayed. This variable is also considered in that validation

        const payload: PayloadListings = {
          shop_id: null, // Saved in laravel session
          limit: 1_000_000_000,
          offset: 0,
          order_by: sortBy.value ?? 'title',
          sort_direction: sortDirection.value ?? 'asc',
          shop_section_id: shopSectionId.value,
          search_term: searchTermToSend,
          advance_filter_option: null,
        };

        response = await axios.get('/listings/changes-v2', {
          params: {
            tracking: true,
            ...payload,
          },
          signal,
        });

        const { data, count } = response.data;

        // Stored for pagination and table sorting
        trackingListAll.value = data.map((item: ListingItem, index: number) => ({ ...item, id: index + 1 })); // Used for pagination, because all data retrieved in a single request

        const offset = rowsPerPage.value * (currentGlobalPage.value - 1);

        dataListings.value = data.slice(offset, offset + rowsPerPage.value).map((item: ListingItem, index: number) => ({ ...item, id: index + 1 }));

        totalPageRecords.value = count;

      }

    } catch (e) {
      totalPageRecords.value = 0;

      if (e.code !== 'ERR_CANCELED') {
        const statusCode = e.response?.status ? `${e.response.status}` : '';
        const errorCode = statusCode ? `${statusCode} LISTING_DATA_FETCH_FAILED` : 'LISTING_DATA_FETCH_FAILED';
        toast.add({
          severity: 'error',
          summary: 'Unable to get Listing data',
          detail: e.response?.data.message || e.response?.data.error || 'Unknown error occurred',
          life: 5000,
          errorCode,
        });
      }

    } finally {

      isLoading.value = false;

      if (currentRequestListingData.value) {
        currentRequestListingData.value = null;
      }

    }

  };

  const getAllListingsForExport = async (
    state: ListingState,
    isTrackingOnly: boolean,
    payload?: Partial<PayloadListings>,
  ): Promise<ListingItem[]> => {
    try {
      await sanctumCookie();

      const merged: PayloadListings = {
        shop_id: null, // Saved in laravel session
        limit: 1_000_000_000,
        offset: payload?.offset ?? 0,
        order_by: payload?.order_by ?? 'title',
        sort_direction: payload?.sort_direction ?? 'asc',
        shop_section_id: payload?.shop_section_id as number,
        search_term: payload?.search_term,
        advance_filter_option: null,
      };

      if (isTrackingOnly) {
        const response = await axios.get(`/listings/changes-v2`, {
          params: {
            tracking: true,
            ...merged,
          },
        });
        const { data } = response.data;

        return data.map((item: ListingItem, index: number) => ({ ...item, id: index + 1 }));
      }

      const response = await axios.post(`/listings/${state}`, merged);
      const { data } = response.data;

      return data.map((item: ListingItem, index: number) => ({ ...item, id: index + 1 }));

    } catch (error) {
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} EXPORT_DATA_FETCH_FAILED` : 'EXPORT_DATA_FETCH_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Unable to fetch export data',
        detail: 'Failed to retrieve all listings for export',
        life: 5000,
        errorCode,
      });
      throw error;
    }
  };

  const filterColumnByTabSelected = (activeTabName: ListingState): ConfigListingColumns => {

    switch (activeTabName) {
      case 'active': {

        const activeColumns = { ...TABLE_LISTINS_CONFIG_COLUMNS_ACTIVE };

        // Free users should NOT have access to the Grade column, instead they will see the Image (thumbnail) column
        activeColumns.grade = {
          ...activeColumns.grade,
          visible: !isFreePlan,
        };

        return activeColumns;
      }

      case 'draft': {
        return TABLE_LISTINS_CONFIG_COLUMNS_DRAFT;
      }

      case 'expired': {
        return TABLE_LISTINS_CONFIG_COLUMNS_EXPIRED;
      }

      case 'sold_out': {
        return TABLE_LISTINS_CONFIG_COLUMNS_SOLD_OUT;
      }

      default: {
        return TABLE_LISTINS_CONFIG_COLUMNS_ACTIVE;
      }

    }

  };

  const updateColumns = (columns: ConfigListingColumns) => {

    customColumns.value = columns;
    dataListings.value = [...dataListings.value]; // Simulate reload for re-render table and fix column ordering

  };

  const getColumnsPreferences = async (state: ListingState) => {
    let keyName = '';
    const newPrefs: ConfigListingColumns = {};

    switch (state) {
      case 'active': {
        keyName = NAME_ACTIVE_TABLE_CONFIG_COLUMNS;
        break;
      }
      case 'draft': {
        keyName = NAME_DRAFT_TABLE_CONFIG_COLUMNS;
        break;
      }
      case 'expired': {
        keyName = NAME_EXPIRED_TABLE_CONFIG_COLUMNS;
        break;
      }
      case 'sold_out': {
        keyName = NAME_SOLD_OUT_TABLE_CONFIG_COLUMNS;
        break;
      }
    }

    try {
      const response = await axios.get(`/account/user-preferences/${keyName}`);
      const { data } = response;

      if(data) {
        const defaultPrefs = filterColumnByTabSelected(state);
        const parsedPrefs = JSON.parse(data?.preference_value);

        // Filter out any columns that no longer exist in the default preferences
        Object.keys(parsedPrefs).forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(defaultPrefs, field)) {
            newPrefs[field] = { 
              ...parsedPrefs[field],
              sortable: defaultPrefs[field].sortable,
            };
          }
        });

        // Add missing preferences from defaultColumnPreferences at the end
        const maxIndex = Math.max(
          ...Object.values(newPrefs).map(pref => pref.index ?? 0),
          0,
        );

        let nextIndex = maxIndex + 1;

        Object.keys(defaultPrefs).forEach((field) => {
          if (!Object.prototype.hasOwnProperty.call(newPrefs, field)) {
            // Add new column at the end with the next available index
            const defaultPref = { ...defaultPrefs[field] };
            defaultPref.index = nextIndex;
            newPrefs[field] = defaultPref;
            nextIndex++;
          }
        });

        // For Active tab: Apply plan-based restrictions for grade and thumbnail columns
        // Free users should see thumbnail (image) column, paid users should see grade column
        if (state === 'active') {
          if (newPrefs.grade) {
            newPrefs.grade = {
              ...newPrefs.grade,
              visible: !isFreePlan,
            };
          }
          if (newPrefs.thumbnail) {
            newPrefs.thumbnail = {
              ...newPrefs.thumbnail,
              visible: isFreePlan,
            };
          }
        }

        customColumns.value = newPrefs;
      }
    } catch (error) {
      // Continue with default columns (customColumns.value remains unchanged)
    }

  };

  const getSortPreferences = async (state: ListingState) => {
    const columnsRemoved = ['actual_sales']; // Columns that no longer exist in the table
    let keyName = '';

    switch (state) {
      case 'active': {
        keyName = NAME_ACTIVE_TABLE_SORT_PREFERENCES;
        break;
      }
      case 'draft': {
        keyName = NAME_DRAFT_TABLE_SORT_PREFERENCES;
        break;
      }
      case 'expired': {
        keyName = NAME_EXPIRED_TABLE_SORT_PREFERENCES;
        break;
      }
      case 'sold_out': {
        keyName = NAME_SOLD_OUT_TABLE_SORT_PREFERENCES;
        break;
      }
    }

    try {
      const response = await axios.get(`/account/user-preferences/${keyName}`);
      const { data } = response;

      if (data) {
        const sortPreferences: SortPreferences =  JSON.parse(data.preference_value);
        if(!columnsRemoved.includes(sortPreferences.sortBy as string)){
          sortBy.value = sortPreferences.sortBy;
          sortDirection.value = sortPreferences.sortDirection;
        }
      }
    } catch (error) {
      // Continue with default sort (sortBy and sortDirection remain unchanged)
    }

  };

  const saveSortPreferences = async (state: ListingState) => {
    let keyName = '';

    switch (state) {
      case 'active': {
        keyName = NAME_ACTIVE_TABLE_SORT_PREFERENCES;
        break;
      }
      case 'draft': {
        keyName = NAME_DRAFT_TABLE_SORT_PREFERENCES;
        break;
      }
      case 'expired': {
        keyName = NAME_EXPIRED_TABLE_SORT_PREFERENCES;
        break;
      }
      case 'sold_out': {
        keyName = NAME_SOLD_OUT_TABLE_SORT_PREFERENCES;
        break;
      }
    }

    try {
      const sortPreferences: SortPreferences = {
        sortBy: sortBy.value,
        sortDirection: sortDirection.value,
      };

      await axios.post('/account/user-preferences', {
        preference_name: keyName,
        preference_value: JSON.stringify(sortPreferences),
      });
    } catch (error) {
      // Fail silently - sort preferences are not critical
    }

  };

  const trackOrUntrackListingItem = async (listingId: number, track: boolean) => {

    try {
      const response = await axios.post(`/listings/changes`, {
        listing_id: listingId,
        track,
      });

      const { success, message } = response.data;

      toast.add({
        severity: success ? 'success' : 'warn',
        summary: message,
        life: 5000,
      });

      if (success) {

        if (!checkedTrackingOnly.value) {
          updateTrackingButtonState(listingId, track);
        }else{
          removeListingById(listingId);
        }

        await getTrackingQuota();
        trackEvent(mixPanelEvents.CLICKED_TRACKED_LISTING, {
          Module: 'Listing Audit',
          'Erank Version': 3,
          'Listings Currently Tracked': trackingUsage.value,
        });

      }

    } catch (e) {
      const statusCode = e.response?.status ? `${e.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} LISTING_UPDATE_FAILED` : 'LISTING_UPDATE_FAILED';
      toast.add({
        severity: 'error',
        summary: e.response?.data.error || 'Unknown error occurred',
        life: 5000,
        errorCode,
      });

      return 0;

    }
  };

  const getTrackingQuota = async (): Promise<void> => {

    try {
      const response = await axios.get(`/quota/none-daily`);
      const { feature_quota, current_usage } = response.data.listing_changes;

      trackingQuota.value = feature_quota;
      trackingUsage.value = current_usage;

      userReachTrackingQuota.value = current_usage >= feature_quota;

      showNoTrackingListings.value = current_usage === 0;

    } catch (e) {
      const statusCode = e.response?.status ? `${e.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} LISTING_QUOTA_FETCH_FAILED` : 'LISTING_QUOTA_FETCH_FAILED';
      toast.add({
        severity: 'error',
        summary: `Unable to get listing quota`,
        detail: e.response?.data.error || 'Unknown error occurred',
        life: 5000,
        errorCode,
      });
    }
  };

  const getTabNumbers = async () => {
    try {
      const response = await axios.get<{ state: string; total: number }[]>(`/listings/counts`);
      const { data } = response;

      if (data.length) {
        data.forEach(item => {
          // This function is invoked when the module loads, and the active count will be retrieved directly from the data table.
          //if(item.state !== 'active') {
          updateTabBadge(item.state, item.total);
          //}
        });
      }
    } catch (e) {
      const statusCode = e.response?.status ? `${e.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} LISTING_TAB_DATA_FETCH_FAILED` : 'LISTING_TAB_DATA_FETCH_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Unable to get Listing data',
        detail: e.response?.data.error || 'Unknown error occurred',
        life: 3000,
        errorCode,
      });
    }
  };

  const updateTabBadge = (state: string, count: number) => {
    switch (state) {
      case 'active':
        activeCount.value = count;
        break;
      case 'draft':
        draftCount.value = count;
        break;
      case 'expired':
        expiredCount.value = count;
        break;
      case 'sold_out':
        soldOutCount.value = count;
        break;
    }
  };

  const updateTrackingButtonState = (listingId: number, isTracking: boolean) => {

    isLoading.value = true; // Required for the table to refresh and show/hide the tracking, untracking button

    setTimeout(() => {

      // Update is_trackiing state
      dataListings.value = dataListings.value.map(listing => listing.listing_id === listingId ? { ...listing, is_tracking: isTracking } : listing);

      isLoading.value = false;

    }, 100);

  };

  const removeListingById = (listing_id: number) => {

    isLoading.value = true; // Required for the table to refresh and show/hide the tracking, untracking button

    setTimeout(() => {
      // 1. Remove the listing and re-enumerate
      trackingListAll.value = trackingListAll.value
        .filter(item => item.listing_id !== listing_id)
        .map((listing, index) => ({ ...listing, id: index + 1 }));

      // 2. If no records exist, navigate back to Listings (checkedTrackingOnly has a watch to handle this)
      if (trackingListAll.value.length === 0) {
        checkedTrackingOnly.value = false;
        dataListings.value = [];
        isLoading.value = false;
        return;
      }

      totalPageRecords.value = trackingListAll.value.length;

      // 3. Adjust the page if needed
      if (dataListings.value.length === 1 && currentGlobalPage.value > 1) {
        currentGlobalPage.value--;
      }

      const offset = (rowsPerPage.value * (currentGlobalPage.value - 1));
      dataListings.value = toValue(trackingListAll).slice(offset, offset + rowsPerPage.value);

      isLoading.value = false;

    }, 100);
  };

  const getListingNameColumn = (listingState: ListingState): ListingNameColumnConfig => {

    const config = { ...listingNameColumnConfig };

    if (listingState === 'active') {
      if (config.ending_timestamp) {
        config.ending_timestamp = {
          ...config.ending_timestamp,
          columnName: 'EXPIRY DATE',
          columnTooltip: 'The date the listing will expire.',
        };
      }
    }

    if (listingState === 'sold_out') {
      if (config.state_timestamp) {
        config.state_timestamp = {
          ...config.state_timestamp,
          columnName: 'DATE SOLD OUT',
          columnTooltip: 'The date this listing was sold out.',
        };
      }
    }

    if (listingState === 'expired') {
      if (config.ending_timestamp) {
        config.ending_timestamp = {
          ...config.ending_timestamp,
          columnName: 'DATE EXPIRED',
          columnTooltip: 'Date when listing expired.',
        };
      }
    }

    return config;
  };

  const resetPerPage = (defaultValue?: number) => {
    if (paginatorComponentRef.value) {
      paginatorComponentRef.value.resetPerPage(defaultValue);
    }
  };

  // Reset paginator will reload the table with actual payload
  const setPage = (page: number) => {
    if (paginatorComponentRef.value) {
      paginatorComponentRef.value.setPage(page);
    }
  };

  const setCurrentState = (state: ListingState) => {
    currentState.value = state;
  };

  const setCurrentRowsLimit = (rowsLimit: number) => {
    currentRowsLimit.value = rowsLimit;
  };

  const setCurrentOffset = (offset: number) => {
    currentOffset.value = offset;
  };

  const trackingColorText = computed(() => {

    const targetValue = trackingQuota.value;
    let currentValue = trackingUsage.value;

    if (targetValue <= 0) {
      return '';
    }
    if (currentValue < 0) {
      currentValue = 0;
    }

    const percentageReached = (currentValue / targetValue) * 100;

    if (percentageReached >= 100) {
      return 'text-[--v3-red-red-400]';
    } else if (percentageReached >= 90) {
      return 'text-[--v3-yellow-yellow-600]';
    } else {
      return '';
    }

  });

  const showNoListingMessage = computed(() => !isLoading.value && dataListings.value.length === 0 && !keepShowingTrackingAndSection.value);

  const sectionDropDownItems = computed(() => (sections.value || [])
    .filter(e => e.active_listing_count !== 0) // do now show sections with 0 listings
    .map((e) => toRaw({ //
      name: `${e.section_name} (${e.active_listing_count})`,
      section_id: e.shop_section_id,
    })));

  const getRowsPerPage = () => {
    const stored = localStorage.getItem(ROWS_PER_PAGE);
    if (stored) {
      const parsedValue = parseInt(stored, 10);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        return parsedValue;
      }
    }
    return 100;
  };

  const getPageNumber = (state: ListingState) => {
    const stored = localStorage.getItem(`${PAGE_NUMBER}_${state}`);
    if (stored) {
      const parsedValue = parseInt(stored, 10);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        return parsedValue;
      }
    }
    return 1;
  };

  return {
    // Variables
    checkedTrackingOnly,
    currentGlobalPage,
    currentRequestListingData,
    currentRowsLimit,
    currentOffset,
    customColumns,
    currentState,
    dataListings,
    isLoading,
    isLoadingSections,
    keepShowingTrackingAndSection,
    paginatorComponentRef,
    quickSearch,
    quickSearchListingId,
    sections,
    shopSectionId,
    showNoTrackingListings,
    sortBy,
    sortDirection,
    totalPageRecords,
    trackingListAll,
    trackingQuota,
    trackingUsage,
    userReachTrackingQuota,
    tabBadges,
    activeCount,
    draftCount,
    expiredCount,
    soldOutCount,
    // Computed
    sectionDropDownItems,
    showNoListingMessage,
    trackingColorText,
    // Functions
    filterColumnByTabSelected,
    getAllListingsForExport,
    getColumnsPreferences,
    getSortPreferences,
    saveSortPreferences,
    getListingItems,
    getListingNameColumn,
    getSections,
    getTabNumbers,
    getTrackingQuota,
    removeListingById,
    resetPerPage,
    setPage,
    setCurrentState,
    setCurrentRowsLimit,
    setCurrentOffset,
    trackOrUntrackListingItem,
    updateColumns,
    updateTrackingButtonState,
    getRowsPerPage,
    getPageNumber,
  };
});


export const useListingTableConfigStore = defineStore('listingTableConfig', () => {
  const rowsPerPage = ref<number>(50);
  const shopSectionId = ref<number | null>(null);
  const showTrackingOnly = ref<boolean>(false);
  const isLoadingShopSectionId = ref<boolean>(localStorage.getItem(SHOP_SECTION_ID) !== null && localStorage.getItem(SHOP_SECTION_ID) != '');
  const quickSearch = ref<string>(storage.get<string>(SEARCH_QUERY_KEY) ?? '');
  const pageSelected = ref<Record<ListingState, number>>({
    active: 1,
    draft: 1,
    expired: 1,
    sold_out: 1,
    'thumbnail-preview': 1,
  });

  const resetPageNumber = () => {
    pageSelected.value = {
      active: 1,
      draft: 1,
      expired: 1,
      sold_out: 1,
      'thumbnail-preview': 1,
    };
  };

  const resetShowTrackingOnly = () => {
    showTrackingOnly.value = false;
  };

  const resetRowsPerPage = () => {
    const stored = localStorage.getItem(ROWS_PER_PAGE);
    if (stored) {
      const parsedValue = parseInt(stored, 10);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        rowsPerPage.value = parsedValue;

        return;
      }
    }
    rowsPerPage.value = 10;
  };

  const resetShopSectionId = () => {
    shopSectionId.value = null;
  };

  const resetQuickSearch = () => {
    quickSearch.value = '';
    storage.remove(SEARCH_QUERY_KEY);
  };

  watch(showTrackingOnly, () => {
    localStorage.setItem(SHOW_TRACKING_ONLY, showTrackingOnly.value.toString());
  });

  watch(shopSectionId, () => {
    localStorage.setItem(SHOP_SECTION_ID, shopSectionId.value?.toString() || '');
  });

  watch(rowsPerPage, () => {
    localStorage.setItem(ROWS_PER_PAGE, rowsPerPage.value.toString());
  });

  watch(pageSelected, () => {
    Object.entries(pageSelected.value).forEach(([state, page]) => {
      localStorage.setItem(`${PAGE_NUMBER}_${state}`, page.toString());
    });
  });

  const loadShopSectionId = (sectionItems?: { section_id: number }[]) => {
    let data : number | boolean = false;
    const stored = localStorage.getItem(SHOP_SECTION_ID);

    if (stored) {
      isLoadingShopSectionId.value = true;
      const parsedValue = parseInt(stored, 10);
      
      // Check if stored value exists in sectionItems (if provided)
      if (sectionItems && sectionItems.length > 0) {
        const isValidSection = sectionItems.some(item => item.section_id === parsedValue);
        if (!isValidSection) {
          // Clear invalid stored value
          localStorage.removeItem(SHOP_SECTION_ID);
          
          isLoadingShopSectionId.value = false;

          return false;
        }
      } else {
        isLoadingShopSectionId.value = false;

        return false;
      }
      
      if (!isNaN(parsedValue) && parsedValue > 0) {
        data = parsedValue;
      }
    }

    isLoadingShopSectionId.value = false;

    return data;
  };

  const loadShowTrackingOnly = () => {
    const stored = localStorage.getItem(SHOW_TRACKING_ONLY);
    if (stored !== null) {
      // Accept 'true'/'false' and '1'/'0'
      showTrackingOnly.value = stored === 'true' || stored === '1';
    }
  };

  const loadRowsPerPage = () => {
    const stored = localStorage.getItem(ROWS_PER_PAGE);
    if (stored) {
      const parsedValue = parseInt(stored, 10);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        rowsPerPage.value = parsedValue;
      }
    }
  };

  const loadPageNumber = (state: ListingState) => {
    const stored = localStorage.getItem(`${PAGE_NUMBER}_${state}`);
    if (stored) {
      const parsedValue = parseInt(stored, 10);
      if (!isNaN(parsedValue) && parsedValue > 0) {
        pageSelected.value[state] = parsedValue;
      }
    }
  };

  const paginationPreferencesSlug = (state: ListingState) => {
    switch (state) {
      case 'active': {
        return {
          rows: NAME_ACTIVE_TABLE_PAGINATION_PREFERENCES_ROWS,
          page: NAME_ACTIVE_TABLE_PAGINATION_PREFERENCES_PAGE,
        };
      }
      case 'draft': {
        return {
          rows: NAME_DRAFT_TABLE_PAGINATION_PREFERENCES_ROWS,
          page: NAME_DRAFT_TABLE_PAGINATION_PREFERENCES_PAGE,
        };
      }
      case 'expired': {
        return {
          rows: NAME_EXPIRED_TABLE_PAGINATION_PREFERENCES_ROWS,
          page: NAME_EXPIRED_TABLE_PAGINATION_PREFERENCES_PAGE,
        };
      }
      case 'sold_out': {
        return {
          rows: NAME_SOLD_OUT_TABLE_PAGINATION_PREFERENCES_ROWS,
          page: NAME_SOLD_OUT_TABLE_PAGINATION_PREFERENCES_PAGE,
        };
      }
      default: {
        return {
          rows: NAME_THUMBNAIL_PREVIEW_TABLE_PAGINATION_PREFERENCES_ROWS,
          page: NAME_THUMBNAIL_PREVIEW_TABLE_PAGINATION_PREFERENCES_PAGE,
        };
      }
    }
  };

  return {
    rowsPerPage,
    shopSectionId,
    showTrackingOnly,
    pageSelected,
    quickSearch,
    isLoadingShopSectionId,
    loadRowsPerPage,
    loadPageNumber,
    loadShopSectionId,
    loadShowTrackingOnly,
    resetPageNumber,
    resetRowsPerPage,
    resetShopSectionId,
    resetShowTrackingOnly,
    resetQuickSearch,
    paginationPreferencesSlug,
  };
});
